// Which experience this person sees in these books (PRD 45).
//
// A singular resource with no id in the path: it is always the caller's own
// preference in the business X-Business-Id names, so there is no addressable
// way to read or write anybody else's.
//
// Readable AND writable by every role, read_only included — an experience is a
// view preference, not a permission, so this is deliberately not behind
// useBusinessMutability.

import { businessApiGet, businessApiPut } from '../utils/apiUtils';
import { parseData } from './businessApiCommon';
import { BusinessExperience, UpdateBusinessExperienceParams } from '../types/businessApi';

const BASE = '/api/v1/business/experience';

/**
 * The role experiences (api Experiences::Catalog::ROLE_EXPERIENCES, PRD 89
 * §4.12): what a dispatcher or technician is SHOWN, whatever anyone prefers.
 * Not a choice — never offered, never saved — so nothing that lets a person
 * change their experience applies to one: the api ignores their preference
 * and `nav_expanded`, and refuses them PUT /experience with role_forbidden.
 */
export const ROLE_EXPERIENCE_KEYS: readonly string[] = [
  'field_technician',
  'field_dispatcher',
  'pos_register',
  // PRD 121 §4.10: personal services' front desk and provider.
  'ps_front_desk',
  'ps_provider',
];

export const isRoleExperience = (experience: Pick<BusinessExperience, 'experience_key'>): boolean =>
  ROLE_EXPERIENCE_KEYS.includes(experience.experience_key);

class BusinessExperienceService {
  /** GET /experience — the resolved view plus both catalogues. */
  async get(): Promise<BusinessExperience> {
    return parseData<BusinessExperience>(await businessApiGet(BASE));
  }

  /**
   * PUT /experience — whole-object and idempotent. Sending an experience with
   * no customisation IS the reset, so "Reset to <experience>" needs no second
   * endpoint and cannot half-apply.
   */
  async update(params: UpdateBusinessExperienceParams): Promise<BusinessExperience> {
    return parseData<BusinessExperience>(await businessApiPut(BASE, params));
  }
}

export const businessExperienceService = new BusinessExperienceService();
export default businessExperienceService;
